import { Selector } from 'react-redux';
import { createSelector } from 'reselect';

import {
  APP_CONFIG,
  APP_OPTIONS,
  CHECK_AUTH,
  CHECK_PASSWORD_RESET_TOKEN,
  CHECK_SSO_AZURE_CONFIG,
  CHECK_SSO_JWT_CONFIG,
  CHECK_SSO_SAML_CONFIG,
  CLEAR_MFA_CONTEXT,
  FEATURE_BY_NAME,
  FORGOT_PASSWORD,
  GET_CUSTOM_MENU_ITEMS_ACTION,
  LOGIN,
  LOGOUT,
  PRODUCT_CURRENCY,
  SET_APP_LOADING,
  SET_AUTH_LOADING,
  SET_CHECK_PASSWORD_RESET_TOKEN_LOADING,
  SET_FEATURE_LOADING,
  SET_LOGIN_LOADING,
  SET_MENU_LOADING,
  SET_MFA_CONTEXT,
  SET_OPTIONS_LOADING,
  SET_POINTS_LOADING,
  SIGNUP,
  UPDATE_USER_CONDITIONS_AND_TERMS,
  USER_POINTS,
} from 'actions/app.actions';
import {
  ActionType,
  ErrorTypes,
  ICustomMenuItem,
  OptionType,
  RoleTypes,
  StoreOptionsType,
} from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';

type FeatureType = {
  data?: {
    enabled: boolean;
    attrs: any;
    configuration?: Record<string, string> | null;
  };
  error?: ErrorTypes | null;
  isLoading: boolean;
};

type passwordResetTokenType = {
  isLoading: boolean;
  isValid?: boolean;
  email?: string;
  error?: ErrorTypes | null;
};

export interface IAppState {
  loading: boolean;
  auth: {
    isAuthenticated: boolean;
    mfa?: {
      tempToken?: string;
    };
    userInfos: {
      isCGUAccepted?: boolean;
      id?: string;
      username?: string;
      status?: number;
      userRoles?: RoleTypes[];
      userGroups?: { id: number; name: string }[];
      points?: number;
      pointsLoading: boolean;
      profile?: {
        firstName: string;
        lastName: string;
      };
    };
    hasSsoAzureConfigured: boolean;
    hasSsoJwtConfigured: boolean;
    hasSsoSamlConfigured: boolean;
    loading: boolean;
    loginLoading: boolean;
    error: ErrorTypes | null;
    passwordResetToken: passwordResetTokenType;
  };
  config: { [key: string]: any };
  configError: ErrorTypes | null;
  productCurrency: string | null;
  productCurrencyError: ErrorTypes | null;
  menu: {
    loading: boolean;
    customMenuItems: ICustomMenuItem[];
  };
  options: StoreOptionsType;
  features: {
    [featureName: string]: FeatureType;
  };
}

export const initialState: IAppState = {
  loading: true,
  auth: {
    mfa: {
      tempToken: undefined,
    },
    passwordResetToken: {
      isLoading: false,
      error: null,
    },
    isAuthenticated: false,
    userInfos: {
      pointsLoading: false,
    },
    hasSsoAzureConfigured: false,
    hasSsoJwtConfigured: false,
    hasSsoSamlConfigured: false,
    loading: true, // true when checking auth (/me) — initialisé à true pour bloquer PrivateRoute jusqu'à la fin du GET /me au démarrage
    loginLoading: false, // true when logging (/login_check or forgot password, create acc etc.)
    error: null,
  },
  config: {},
  configError: null,
  productCurrency: null,
  productCurrencyError: null,
  menu: {
    loading: false,
    customMenuItems: [],
  },
  options: {
    list: [],
    isLoading: false,
    error: null,
  },
  features: {},
};

export const appReducerSelector = (state: { app: IAppState }): IAppState =>
  state.app;

export const isAuthenticatedSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.isAuthenticated,
);

export const authLoadingSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.loading || false,
);

export const ssoAzureSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoAzureConfigured,
);

export const ssoJwtSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoJwtConfigured,
);

export const ssoSamlSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.hasSsoSamlConfigured,
);

export const passwordResetTokenSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.passwordResetToken,
);

export const loginLoadingSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.loginLoading || false,
);

export const userInfosSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.userInfos || { pointsLoading: false },
);

export const userStatusSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.userInfos?.status,
);

export const STATUS_NEED_TO_FILL_FIRST_LOGIN_FORM = 16;

export const appConfigSelector = createSelector(
  appReducerSelector,
  (app) => app.config || {},
);

export const configErrorSelector = createSelector(
  appReducerSelector,
  (app) => app.configError,
);

export const appOptionsSelector = createSelector(
  appReducerSelector,
  (app) => app.options || {},
);

export const currencySelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): string =>
    options.list.find((option: OptionType) => option.name === 'CURRENCY_NAME')
      ?.value ?? '',
);

export const clientIDLabelSelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): string =>
    options.list.find((option: OptionType) => option.name === 'clientID_label')
      ?.value ?? '',
);

export interface IColorMap {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  accent?: string;
  background?: string;
}

export const colorsSelector = createSelector(
  appOptionsSelector,
  (options: StoreOptionsType): IColorMap =>
    options.list.reduce((acc: IColorMap, option: OptionType) => {
      const value = option.value;

      switch (option.name) {
        case '$primaryHue':
          return { ...acc, primary: value };
        case '$secondaryHue':
          return { ...acc, secondary: value };
        case '$thirdHue':
          return { ...acc, tertiary: value };
        case '$accentHue':
          return { ...acc, accent: value };
        case '$backgroundHue':
          return { ...acc, background: value };
        default:
          return acc;
      }
    }, {}),
);

export const menuSelector = createSelector(
  appReducerSelector,
  (app) => app.menu || {},
);

export const featureSelector = (
  featureName: string,
): Selector<{ app: IAppState }, FeatureType> =>
  createSelector(appReducerSelector, (app) => app.features[featureName]);

export const productCurrencySelector = createSelector(
  appReducerSelector,
  (app) => app.productCurrency,
);

export const mfaTempTokenSelector = createSelector(
  appReducerSelector,
  (app) => app.auth.mfa?.tempToken,
);

const patchPasswordResetToken = (
  state: IAppState,
  props: Partial<passwordResetTokenType>,
): IAppState => ({
  ...state,
  auth: {
    ...state.auth,
    passwordResetToken: {
      ...state.auth.passwordResetToken,
      ...props,
    },
  },
});

export const appReducer = (
  state: IAppState = initialState,
  action: ActionType,
): IAppState => {
  const { payload } = action;

  switch (action.type) {
    case LOGOUT:
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: false,
          userInfos: {
            pointsLoading: false,
          },
          mfa: {
            tempToken: undefined,
          },
          error: null,
        },
      };
    case SET_APP_LOADING:
      return {
        ...state,
        loading: payload.isLoading,
      };
    case SET_AUTH_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          loading: payload.isLoading,
        },
      };
    case SET_LOGIN_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          loginLoading: payload.isLoading,
        },
      };
    case CHECK_AUTH[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: true,
          userInfos: payload.userInfos,
        },
      };
    case CHECK_SSO_AZURE_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoAzureConfigured: payload.hasSsoAzureConfigured,
        },
      };
    case CHECK_SSO_JWT_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoJwtConfigured: payload.hasSsoJwtConfigured,
        },
      };
    case CHECK_SSO_SAML_CONFIG[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          hasSsoSamlConfigured: payload.hasSsoSamlConfigured,
        },
      };
    case CHECK_PASSWORD_RESET_TOKEN[SUCCESS]:
      return patchPasswordResetToken(state, {
        isValid: payload.isValid,
        email: payload.email,
        error: null,
      });
    case CHECK_PASSWORD_RESET_TOKEN[FAILURE]:
      return patchPasswordResetToken(state, {
        error: payload.error,
        isValid: undefined,
        email: undefined,
      });
    case SET_CHECK_PASSWORD_RESET_TOKEN_LOADING:
      return patchPasswordResetToken(state, {
        isLoading: payload.isLoading,
      });
    case SIGNUP[FAILURE]:
      return {
        ...state,
        auth: {
          ...state.auth,
          error: payload.error,
        },
      };
    case LOGIN[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          isAuthenticated: true,
        },
      };
    case FORGOT_PASSWORD[SUCCESS]:
    case SIGNUP[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          error: null,
        },
      };
    case APP_CONFIG[SUCCESS]:
      return {
        ...state,
        config: {
          ...payload.data,
          programUrl: payload.data.programUrl.match(/^http(|s):\/\//)
            ? payload.data.programUrl
            : `${window.location.protocol}//${payload.data.programUrl}`,
          appLocale: payload.data.locale,
        },
        configError: null,
      };
    case APP_CONFIG[FAILURE]:
      return {
        ...state,
        config: {},
        configError: payload,
      };
    case PRODUCT_CURRENCY[SUCCESS]:
      return {
        ...state,
        productCurrency: payload.data,
        productCurrencyError: null,
      };
    case PRODUCT_CURRENCY[FAILURE]:
      return {
        ...state,
        productCurrency: null,
        productCurrencyError: payload,
      };
    case SET_OPTIONS_LOADING: {
      return {
        ...state,
        options: {
          ...state.options,
          isLoading: payload.isLoading,
        },
      };
    }
    case APP_OPTIONS[SUCCESS]: {
      return {
        ...state,
        options: {
          ...state.options,
          list: payload.data,
          error: null,
        },
      };
    }
    case APP_OPTIONS[FAILURE]: {
      return {
        ...state,
        options: {
          ...state.options,
          list: [],
          error: payload,
        },
      };
    }
    case SET_MENU_LOADING:
      return {
        ...state,
        menu: {
          ...state.menu,
          loading: payload.isLoading,
        },
      };
    case GET_CUSTOM_MENU_ITEMS_ACTION[SUCCESS]: {
      const { menuItems } = payload;
      return {
        ...state,
        menu: {
          ...state.menu,
          customMenuItems: menuItems.sort(
            (a: ICustomMenuItem, b: ICustomMenuItem) => a.position - b.position,
          ),
        },
      };
    }
    case USER_POINTS[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            points: payload.points,
          },
        },
      };
    case UPDATE_USER_CONDITIONS_AND_TERMS[SUCCESS]:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            isCGUAccepted: payload.user.isCGUAccepted,
          },
        },
      };
    case SET_POINTS_LOADING:
      return {
        ...state,
        auth: {
          ...state.auth,
          userInfos: {
            ...state.auth.userInfos,
            pointsLoading: payload.isLoading,
          },
        },
      };
    case FEATURE_BY_NAME[SUCCESS]:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            data: payload.data,
            error: null,
          },
        },
      };
    case FEATURE_BY_NAME[FAILURE]:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            data: {},
            error: payload.error,
          },
        },
      };
    case SET_FEATURE_LOADING:
      return {
        ...state,
        features: {
          ...state.features,
          [payload.name]: {
            ...(state.features?.[payload.name] || {}),
            isLoading: payload.isLoading,
          },
        },
      };
    case SET_MFA_CONTEXT:
      return {
        ...state,
        auth: {
          ...state.auth,
          mfa: {
            ...state.auth.mfa,
            tempToken: payload.tempToken,
          },
        },
      };
    case CLEAR_MFA_CONTEXT:
      return {
        ...state,
        auth: {
          ...state.auth,
          mfa: {
            tempToken: undefined,
          },
        },
      };
    default:
      return state;
  }
};

export default appReducer;
